import React from "react";
import { Topic, P, H3, H4, UL, Callout, Table, VisualBox } from "@/pages/knowledge/Atoms";

export default function Strategies() {
  return (
    <>
      <Topic id="intraday-strategies" title="Intraday Strategies">
        <P>
          Intraday trading means buying and selling within the same trading day
          (NSE / BSE: 9:15 AM – 3:30 PM). The aim — capture small price moves
          using quick analysis, chart patterns and indicators.
        </P>

        <H3>1. Breakout trading</H3>
        <P>
          When a stock breaks a key support or resistance with strong volume, a
          new trend begins. Infosys consolidating between ₹1,520–₹1,550 — break
          above ₹1,550 with volume → buy targeting ₹1,565–1,575.
        </P>

        <H3>2. Moving Average crossover</H3>
        <P>
          On a 15-min HDFC Bank chart — 20-period MA crosses above 50-period MA
          → buy. Crosses below → sell.
        </P>

        <H3>3. Bank Nifty intraday short straddle</H3>
        <Callout kind="warn">
          Sell ATM Call &amp; Put simultaneously (e.g., 57,000 CE and 57,000 PE)
          when volatility is high. Profit if Bank Nifty stays within a narrow
          range. <b>High risk — use strict stop loss; experienced traders only.</b>
        </Callout>

        <H3>4. Algo-based VWAP momentum</H3>
        <P>
          When price &gt; VWAP and volume &gt; average → algo auto-buys.
          Price &lt; VWAP → algo exits / sells. Used to scalp 0.3–0.7% moves on
          Bank Nifty and Nifty.
        </P>

        <H3>5. RSI reversal</H3>
        <P>
          On a 5-min chart, RSI &lt; 30 → oversold → buy for bounce. RSI &gt;
          70 → overbought → sell.
        </P>

        <H3>6. India VIX-based volatility breakout</H3>
        <P>
          VIX jumps from 12 → 16 → buy ATM Call/Put on Nifty (option buyer
          edge). VIX falls 18 → 14 → sell options (premium decay edge).
        </P>
      </Topic>

      <Topic id="positional-strategies" title="Positional Strategies">
        <P>
          Positional trading holds trades for days, weeks or months — combining
          technical and fundamental analysis. Suitable for working professionals
          and swing traders.
        </P>

        <H3>1. Trend following</H3>
        <P>
          Tata Motors rising steadily above its 50-day MA → buy near dips. Exit
          on trend reversal. Tools: 50-day &amp; 200-day MA, RSI above 50.
        </P>

        <H3>2. Swing trading</H3>
        <P>
          Capture short-term moves inside larger trends. Nifty corrects 26,000 →
          25,800 in an uptrend → buy near 25,800, target 26,200. Tools: Fibonacci
          levels, RSI divergence, support &amp; resistance.
        </P>

        <H3>3. Bull Call Spread (positional)</H3>
        <VisualBox title="EXAMPLE — NIFTY AT 26,000">
          <UL>
            <li>Buy 26,000 CE @ ₹180</li>
            <li>Sell 26,200 CE @ ₹90</li>
            <li>Max profit ₹110 × 75 = ₹8,250</li>
            <li>Max loss ₹90 × 75 = ₹6,750</li>
          </UL>
        </VisualBox>

        <H3>4. Trend-capture algo</H3>
        <P>
          If 50-day MA &gt; 200-day MA and RSI &gt; 60 → algo enters long.
          Automatically exits when price drops below 50-day MA. Common in
          large-cap quant-backed systems.
        </P>

        <H3>5. Mean reversion (quant)</H3>
        <P>
          HDFC trading 5% below its 20-day mean with strong fundamentals → quant
          buys expecting bounce. Entry / exit driven by mathematical models.
        </P>

        <H3>6. MACD + RSI combo</H3>
        <P>
          MACD crosses above signal line → buy. RSI &gt; 50 confirms strength.
          Infosys giving both signals → enter long for 1–2 week swing.
        </P>

        <H3>7. India VIX volatility spreads</H3>
        <P>
          VIX very low (~10) → buy a long straddle (expect volatility rise). VIX
          very high (~20+) → sell an iron condor (expect volatility drop).
          Profits when volatility normalises.
        </P>

        <H3>8. Option adjustment</H3>
        <Callout kind="info">
          Sold Nifty 26,000 CE, market jumps to 26,200. <b>Adjustment:</b> buy
          26,200 CE and sell 26,400 CE → bear call spread; loss is capped and
          margin stabilises.
        </Callout>

        <H3>9. Protective put (hedging)</H3>
        <P>
          You hold ₹5 lakh of HDFC at ₹1,500. Buy 1 lot of HDFC 1500 Put @ ₹20.
          If stock falls to ₹1,420, loss in shares is offset by gain in Put —
          like insurance for your holdings.
        </P>
      </Topic>
    </>
  );
}
